import styled, { keyframes } from 'styled-components';

import { ANIM, COLORS, DELTA_PILL, REDUCED_MOTION, SPACE } from '../../../../theme/tokens';
import { SectionTitle, Text } from '../../../../components';

/** One row's state, as the row paints it. */
export type RowState = 'pending' | 'active' | 'done' | 'failed';

/** The screen inside its Panel: heading, lead, checklist, and (on failure) the reason and retry. */
export const Screen = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[20]};
`;

/** Same shape as the create form's title: SectionTitle with its bottom margin handed to the gap. The
 *  address may be up to 64 characters before the @, so it breaks anywhere rather than overflow. */
export const FormTitle = styled(SectionTitle)`
  margin-bottom: 0;
  min-width: 0;
  overflow-wrap: anywhere;
`;

/** The title and its lead as ONE group, 4px apart: the lead belongs to the title (the same grammar as
 *  the tile's TopGroup), and the screen's 20px gap then runs from the pair to the checklist. */
export const Intro = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[4]};
  min-width: 0;
`;

/** The lead, visible from the first frame. */
export const Lead = styled(Text)`
  font-size: 13px;
  line-height: 1.5;
  color: ${COLORS.textMuted};
`;

/** The checklist: a real list, one row per line, no bullets (the glyph is the bullet). */
export const Checklist = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[12]};
  margin: 0;
  padding: 0;
  list-style: none;
`;

export const Row = styled.li`
  display: flex;
  align-items: center;
  gap: ${SPACE[12]};
  min-width: 0;
`;

/** The glyph's fixed slot, so the labels line up whatever the glyph is. */
export const Glyph = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 20px;
  height: 20px;
`;

/** Pending: a faint dot, the row's place held but nothing happening yet. */
export const Dot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${COLORS.textFaint};
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

/**
 * The row's state, for assistive tech only: visually hidden (the same clip pattern as Checkbox's
 * native input), read after the row's copy as "Done" / "In progress" / "Pending" / "Failed".
 */
export const RowStatus = styled(Text)`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  border: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  white-space: nowrap;
`;

/** In progress: the area's ring spinner (accent arc over a faint track), at row size. */
export const Spinner = styled.span`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid ${COLORS.panelBorder};
  border-top-color: ${COLORS.accent};
  animation: ${spin} ${ANIM.SPIN_MS}ms linear infinite;

  ${REDUCED_MOTION} {
    animation: none;
    /* Still visibly "the current one" without motion: the arc reads as a marker. */
    border-top-color: ${COLORS.accent};
  }
`;

export const RowLabel = styled(Text)<{ $state: RowState }>`
  font-size: 14px;
  line-height: 1.4;
  min-width: 0;
  color: ${(p) =>
    p.$state === 'pending' ? COLORS.textMuted : p.$state === 'failed' ? DELTA_PILL.negative.fg : COLORS.textStrong};
`;

/** The failure: its reason and the retry, under the list, left-aligned like the card's failed body. */
export const FailedBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${SPACE[8]};
`;

export const FailureNote = styled(Text)`
  font-size: 13px;
  line-height: 1.5;
  color: ${DELTA_PILL.negative.fg};
`;

export const TextLink = styled.button`
  appearance: none;
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  color: ${COLORS.textStrong};
  text-decoration: underline;
  text-underline-offset: 3px;
  white-space: nowrap;
`;
